/* eslint-disable no-nested-ternary */
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  RootState, useGetAllBusinessesQuery, useGetUserBusinessesQuery, useGetAllUserEventsQuery,
  useGetWiresRequestsQuery, useGetEventsByQueryQuery, useGetEventsSummaryQuery,
} from '../store';
import BizEventSummary from '../components/BizEventSummary';
import { setSelectedEvent } from '../store/slices/events';
import BizEventSummarySkeleton from '../components/BizEventSummarySkeleton';
import Button from '../components/atoms/Button';
import TitleNumberCard from '../components/atoms/TitleNumberCard';
import { EventPublicStatus } from '../models/event';
import { IconTicket3 } from '../components/icons';
import HorizontalTabs from '../components/atoms/HorizontalTabs';
import OrdersTableSkeleton from '../components/OrdersTableSkeleton';
import EventsSummaryTable from '../components/EventsSummaryTable';
import AdminEvents from './AdminEvents';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state: RootState) => state.auth.user);
  const { data: allBusinesses, isLoading: isAllBusinessesLoading } = useGetAllBusinessesQuery({ query: { active: true } }, { skip: !userData || userData.type !== 'ROOT' });
  const { data: userBusinesses, isLoading: isUserBusinessesLoading } = useGetUserBusinessesQuery({ userId: userData.id }, { skip: !userData || !userData.id || userData.type !== 'ROOT' });
  const { data: events, isLoading: isEventsLoading } = useGetAllUserEventsQuery({ userId: userData.id }, { skip: !userData || userData.type !== 'ROOT' });
  const { data: pendingWires } = useGetWiresRequestsQuery({ pending: true }, { skip: !userData || userData.type !== 'ROOT' });
  const { data: eventsQuantity } = useGetEventsByQueryQuery({}, { skip: !userData || userData.type !== 'ROOT' });
  const [tab, setTab] = useState<'general' | 'finances'>('general');
  const { data: eventsSummary, isLoading: isEventsSummaryLoading } = useGetEventsSummaryQuery(undefined, { skip: tab !== 'finances' });

  const activeEvents = events?.filter((event) => event.public_status === EventPublicStatus.Ready);
  const eventsPendingApproval = eventsQuantity?.filter((event) => event.public_status === EventPublicStatus.PendingApproval);

  const activeBusinesses = userData.type === 'ROOT'
    ? allBusinesses?.filter((business) => business.active === true)
    : userBusinesses?.filter((business) => business.active === true);

  const onGoToEventClick = (eventIndex: number) => {
    if (!activeEvents || eventIndex >= activeEvents.length) {
      return;
    }
    const event = activeEvents[eventIndex];
    if (event) {
      dispatch(setSelectedEvent(event));
      navigate(`/admin/${event.business_id}/events/${event.id}`);
    }
  };

  return (
    <div className="flex flex-col gap-8 my-6">
      <div className="flex flex-row gap-4 items-center">
        <p className="text-[30px] font-medium text-text-primary">¡Hola {userData.username}!</p>
        <IconTicket3 className='w-14 h-14' />
      </div>

      {userData.type === 'ROOT' && <>
        <div className="glass-surface px-3 pb-2 pt-1">
          <HorizontalTabs tabs={[{ label: 'General', onClick: () => setTab('general'), selected: tab === 'general' }, { label: 'Finanzas', onClick: () => setTab('finances'), selected: tab === 'finances' }]} />
        </div>
        {tab === 'general' && (
          <>
            {
              userData.type === 'ROOT' && <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <TitleNumberCard title="Total eventos aprobados" number={activeEvents?.length ?? 0} buttonText="Ver reporte" buttonLink="superadmin" />
                <TitleNumberCard title="Eventos pendientes" number={eventsPendingApproval?.length ?? 0} buttonText="Ver pendientes" buttonLink="superadmin" />
                <TitleNumberCard title="Transferencias pendientes" number={pendingWires?.length ?? 0} buttonText="Ver pendientes" buttonLink="superadmin" />
              </div>
            }

            {((activeEvents?.length ?? 0) > 0 || (activeBusinesses?.length ?? 0) > 0 || isEventsLoading || isAllBusinessesLoading || isUserBusinessesLoading)
              ? <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {isEventsLoading ? (
                  <BizEventSummarySkeleton />
                ) : (
                  <BizEventSummary
                    title="Eventos"
                    subtitle="Últimos eventos cargados"
                    itemButtonText="Ver evento"
                    data={activeEvents ?? []}
                    itemClick={(_, index) => onGoToEventClick(index)}
                  />
                )}
                {(isAllBusinessesLoading || isUserBusinessesLoading) ? (
                  <BizEventSummarySkeleton />
                ) : (
                  <BizEventSummary
                    title="Productoras"
                    subtitle="Productoras activas"
                    mainButtonText='Ir a productoras'
                    itemButtonText='Ver productora'
                    data={activeBusinesses ?? []}
                    mainClick={() => navigate('/admin/businesses')}
                    itemClick={(item) => navigate(`/admin/${item}/events`)}
                  />
                )}
              </div>
              : <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                  <p className="text-text-secondary">¡Todavía no tenés ninguna productora!</p>
                  <p className="text-text-secondary">Si querés organizar tu evento con Avantti, creá una productora</p>
                  <Button variant="primary" onClick={() => navigate('create-business')} className="w-fit">
                    Quiero vender con Avantti
                  </Button>
                </div>
              }
          </>
        )}
        {tab === 'finances' && (
          <div className="glass-surface">
            {isEventsSummaryLoading ? (
              <OrdersTableSkeleton />
            ) : (eventsSummary && eventsSummary.length > 0) && (
              <EventsSummaryTable
                eventsSummary={eventsSummary}
                businesses={activeBusinesses ?? []}
              />
            )}
          </div>
        )}
      </>}
      {userData.type !== 'ROOT' && (
        <div className="flex flex-col gap-6">
          <AdminEvents allUserEvents />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
